import {
  getEngine,
  pairNextRound,
  playerHistory,
  scoreOf,
  type Round,
  type Score,
  type Tournament,
  type TournamentEventBody,
} from '@ctd/core';

export function pendingGames(r: Round): number {
  return r.games.filter((g) => g.result.white === '*' || g.result.black === '*').length;
}

/** Why the next round cannot be paired, or null when it can. */
export function pairingBlocker(t: Tournament): string | null {
  const next = t.rounds.length + 1;
  if (next > t.settings.rounds) {
    return `All ${t.settings.rounds} rounds are paired. Add rounds in Setup to continue.`;
  }
  const last = t.rounds.at(-1);
  if (last !== undefined) {
    const pending = pendingGames(last);
    if (pending > 0) {
      return `Round ${last.number} still has ${pending} game${pending > 1 ? 's' : ''} without a result.`;
    }
  }
  const active = t.players.filter(
    (p) => p.withdrawnAfterRound === null || p.withdrawnAfterRound >= next,
  );
  if (active.length < 2) return 'Add at least two players before pairing.';
  return null;
}

export type PairAttempt =
  | { readonly ok: true; readonly event: TournamentEventBody }
  | { readonly ok: false; readonly error: string };

/** Ask the tournament's engine for the next round, turning any failure into a message. */
export function tryPairNext(t: Tournament): PairAttempt {
  const blocker = pairingBlocker(t);
  if (blocker !== null) return { ok: false, error: blocker };
  try {
    const engine = getEngine(t.settings.system);
    return { ok: true, event: pairNextRound(t, engine) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Each player's score before the given round (1-based), by player id. */
export function scoresBefore(t: Tournament, round: number): Map<string, Score> {
  const out = new Map<string, Score>();
  for (const p of t.players) out.set(p.id, scoreOf(t, playerHistory(t, p, round - 1)));
  return out;
}
